import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { FormProvider, useForm } from "react-hook-form";
import { PaziaDateSelect, PaziaInput } from "components/atoms";
import { TRANCHE_POLICY_TYPE_OPTIONS } from "consts";
import { TRANCHE_POLICY_TYPE } from "consts";
import { PaziaBox } from "components/atoms";
import { PaziaSelect } from "components/atoms";
import { PaziaPrefixInput } from "components/atoms/Inputs";
import { PaziaFormButton, IDropdownOption } from "components/atoms";
import { useCourseQuery } from "../useCourseQuery";
import { useParams } from "react-router-dom";

interface PaymentPolicyFormProps {
  optionsTraches: IDropdownOption[];
  onSubmit: (data: PaymentPolicyFormValues) => Promise<void>;
}

export const PaymentPolicyForm = ({ optionsTraches, onSubmit }: PaymentPolicyFormProps) => {
  const { courseId } = useParams();
  const methods = useForm<PaymentPolicyFormValues>({
    resolver: yupResolver(schema),
  });
  const { course } = useCourseQuery(courseId);
  const { handleSubmit, reset, watch } = methods;
  const policyType = watch(FORM_VALUES.TYPE);
  const isPercentage =
    policyType == TRANCHE_POLICY_TYPE.AddPercentage || policyType == TRANCHE_POLICY_TYPE.SubtractPercentage;

  const onSubmitHandler = async (data: PaymentPolicyFormValues) => {
    await onSubmit(data);
    reset();
  };
  if (!course.data) return null;

  return (
    <FormProvider {...methods}>
      <PaziaBox header="Dodaj nową politykę cenową">
        <form onSubmit={handleSubmit(onSubmitHandler)} className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <PaziaSelect
            required
            label="Transza"
            name={FORM_VALUES.COURSE_TRANCHE_ID}
            options={optionsTraches}
            className="md:col-span-2"
          />
          <PaziaDateSelect required label="Data rozpoczęcia" name={FORM_VALUES.FROM} />
          <PaziaDateSelect label="Data zakończenia" name={FORM_VALUES.TO} />
          <PaziaSelect required label="Rodzaj polityki" name={FORM_VALUES.TYPE} options={TRANCHE_POLICY_TYPE_OPTIONS} />
          <PaziaPrefixInput prefix={isPercentage ? "%" : "PLN"} label="Kwota" name={FORM_VALUES.AMOUNT} />
          <PaziaInput
            type="number"
            label="Powtarzaj"
            name={FORM_VALUES.REPEAT}
            tooltip="Jeśli chcesz powtórzyć politykę płatności, wprowadź liczbę dni. Na przykład: Dla rabatu codwutygodniowego wprowadź liczbę 14."
          />
          <div></div>
          <div></div>
          <div className="ml-auto">
            <PaziaFormButton title="Zapisz" />
          </div>
        </form>
      </PaziaBox>
    </FormProvider>
  );
};

const FORM_VALUES = {
  COURSE_TRANCHE_ID: "courseTrancheId",
  FROM: "from",
  TO: "to",
  TYPE: "type",
  AMOUNT: "amount",
  REPEAT: "repeat",
} as const;
const schema = yup
  .object({
    [FORM_VALUES.FROM]: yup.date().required(),
    [FORM_VALUES.TO]: yup
      .date()
      .nullable()
      // Named tests, not .min()/.max(): InputErrorMessage swaps "min" for a generic text and shows yup's English "max"
      .test(
        "after-start",
        "Data zakończenia nie może być wcześniejsza niż data rozpoczęcia",
        (to, ctx) => !to || !ctx.parent[FORM_VALUES.FROM] || to >= ctx.parent[FORM_VALUES.FROM],
      ),
    [FORM_VALUES.TYPE]: yup.number().required(),
    [FORM_VALUES.AMOUNT]: yup
      .number()
      .typeError("Podaj kwotę")
      .required("Podaj kwotę")
      .test("positive", "Kwota musi być większa od zera", (v) => v == null || v > 0)
      .test(
        "percent-max",
        "Procent nie może przekraczać 100",
        (v, ctx) =>
          v == null ||
          !(
            ctx.parent[FORM_VALUES.TYPE] == TRANCHE_POLICY_TYPE.AddPercentage ||
            ctx.parent[FORM_VALUES.TYPE] == TRANCHE_POLICY_TYPE.SubtractPercentage
          ) ||
          v <= 100,
      ),

    [FORM_VALUES.REPEAT]: yup.string(),
    [FORM_VALUES.COURSE_TRANCHE_ID]: yup.number().required(),
  })
  .required();
export type PaymentPolicyFormValues = yup.InferType<typeof schema>;
